import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Cookies from 'js-cookie';
import { Alert, Col, Row } from 'reactstrap';
import CurrencyFormat from 'react-currency-format';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment/moment';
import * as PropTypes from 'prop-types';
import { BidStatus } from 'stock-vault-admin-panel/src/constants/BidStatus';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { pencesToPounds } from '../../helpers/pencesToPounds';
import AuctionTimeLabel from '../../../myvehicles/AuctionTimeLabel';
import { auctionListType } from '../../../constants/AuctionListType';
import AuctionStatusBox from '../../../myvehicles/AuctionStatusBox';
import CommonButton from '../CommonButton';
import { CommonButtonVariants } from '../../../constants/CommonButtonVariants';
import { AuctionStatus, AuctionStatusName } from '../../../constants/AuctionStatus';
import {
  AUCTION_BID_MAX_BIDS_LIMIT,
  AUCTION_BID_MINIMUM_AMOUNT_IN_PENCE,
} from '../../../constants/AuctionBid';
import auctionIcon from '../../../assets/img/icons/auction.svg';
import auctionBlackIcon from '../../../assets/img/icons/auction2.svg';
import buyNowIcon from '../../../assets/img/icons/offline_bolt.svg';
import buyNowWhiteIcon from '../../../assets/img/icons/offline_bolt_white.svg';
import { AuctionStatusType } from '../../../constants/AuctionStatusType';
import {
  buyNowAuction,
  placeBid,
  requestNextGearLoanCoverage,
} from '../../../search/SearchActions';
import BoxStatusVariants from '../../../constants/BoxStatusVariants';
import infoWhiteIcon from '../../../assets/img/icons/info_white.svg';
import useAuctionCard from '../../hooks/useAuctionCard';
import { formatGBP } from '../../helpers/formatGBP';
import closeIcon from '../../../assets/img/icons/close.svg';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import FormCurrencyInputFieldFixed from '../formFields/FormCurrencyInputFieldFixed';
import { preparePriceFormat } from '../../helpers/preparePriceFormat';
import { getBaseBidInPence } from '../../helpers/AuctionBidHelper';
import AuctionCountdown from '../new/AuctionCountdown';
import { AuctionListTemplateContextType } from '../new/AuctionListTemplate';
import availableIcon from '../../../assets/img/icons/available.svg';
import notAvailableIcon from '../../../assets/img/icons/not-available.svg';
import nextGearCapitalImg from '../../../assets/img/next-gear-capital.svg';
import { showAlert } from '../alert/AlertActions';
import AlertVariant from '../alert/AlertVariant';
import PortalSweetAlert from '../PortalSweetAlert';
import movexLogo from '../../../assets/img/movex-logo.png';
import FormSelectField from '../formFields/FormSelectField';
import { fetchOrganizationLocations } from '../../../addvehicle/AddVehicleActions';
import { LOCATION_ID_COOKIE, setLocationIdCookie } from '../../../constants/LocationCookie';

export const SlotItems = {
  AUCTION_END: 'AUCTION_END',
  TIME_LEFT: 'TIME_LEFT',
  CURRENT_BID: 'CURRENT_BID',
  AUCTION_STATUS_BOX: 'AUCTION_STATUS_BOX',
};

const NextGearLoanCoverageStatus = {
  SUCCEEDED: 'SUCCEEDED',
  FAILED: 'FAILED',
  NOT_INITIALIZED: 'NOT_INITIALIZED',
};

export const DefaultPattern = [
  SlotItems.AUCTION_END,
  SlotItems.TIME_LEFT,
  SlotItems.CURRENT_BID,
  SlotItems.AUCTION_STATUS_BOX,
];

function AuctionMainDescriptionContainer({
  item,
  windowFocus,
  organizationId,
  listType,
  pattern,
  patternSizes,
  isDetailsPage,
  bidBarMessage,
  bidBarColor,
  className,
  auctionTimestamp,
  highestBid,
  proxyBid,
  shouldPriceBeGreen,
  context,
}) {
  const [isPlaceBidType, setIsPlaceBidType] = useState(
    item?.type !== AuctionStatusType.BUY_NOW_ONLY,
  );
  const dispatch = useDispatch();
  const isLoading = useSelector((state) => state.navbarLoader.get('isLoading'));
  const isBuyer = useSelector((state) => state.auth.get('isBuyer'));
  const isSeller = useSelector((state) => state.auth.get('isSeller'));
  const bidsSearchFilter = useSelector((state) => state.myBids.get('filter'));
  const memberId = useSelector((state) => state.auth.get('id'));
  const buyerNextGearId = useSelector((state) => state.auth.get('buyerNextGearId'));
  const [nextGearLoading, setNextGearLoading] = useState(false);
  const [showNextGearBuyerSuccessAlert, setShowNextGearBuyerSuccessAlert] = useState(false);
  const [showNextGearBuyerFailureAlert, setShowNextGearBuyerFailureAlert] = useState(false);

  const organizationLocations = useSelector((state) =>
    state.myOrganization.get('organizationLocations'),
  );

  const locationOptions = useMemo(
    () =>
      (Array.isArray(organizationLocations) ? organizationLocations : []).map((loc) => ({
        value: loc.id,
        label: loc.name || loc.address || loc?.postalCode?.postcode,
        isMain: loc.isMain,
      })),
    [organizationLocations],
  );

  const hasSingleLocation =
    (Array.isArray(organizationLocations) ? organizationLocations.length : 0) <= 1;

  const defaultLocationOption = useMemo(() => {
    if (locationOptions.length === 0) return null;
    const cookieOption = locationOptions.find(
      (opt) => opt.value === Cookies.get(LOCATION_ID_COOKIE),
    );
    return cookieOption || locationOptions.find((opt) => opt.isMain) || locationOptions[0];
  }, [locationOptions]);

  const lockedLocationId = useMemo(
    () =>
      (Array.isArray(item?.bids) ? item.bids : []).find(
        (bid) =>
          bid.bidderOrganizationId === organizationId && bid.bidderOrganizationLocationId,
      )?.bidderOrganizationLocationId || null,
    [item?.bids, organizationId],
  );
  const isLocationLocked = !!lockedLocationId;
  const lockedLocationOption = useMemo(() => {
    if (!lockedLocationId) return null;
    return (
      locationOptions.find((opt) => opt.value === lockedLocationId) || {
        value: lockedLocationId,
        label: '',
      }
    );
  }, [locationOptions, lockedLocationId]);

  useEffect(() => {
    dispatch(fetchOrganizationLocations());
  }, [dispatch]);

  const bidFormRef = useRef(null);
  useEffect(() => {
    if (!bidFormRef.current) return;
    if (isLocationLocked) {
      if (lockedLocationOption) {
        bidFormRef.current.setFieldValue('location', lockedLocationOption);
      }
    } else if (defaultLocationOption && !bidFormRef.current.values?.location) {
      bidFormRef.current.setFieldValue('location', defaultLocationOption);
    }
  }, [isLocationLocked, lockedLocationOption, defaultLocationOption]);

  const isBidAllowed =
    isBuyer &&
    item?.bids?.length < AUCTION_BID_MAX_BIDS_LIMIT &&
    item?.organizationId !== organizationId &&
    moment(item.endsOn).isAfter();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const {
    status,
    bidContext,
    highestBidInPence,
    displayPriceInPence,
    priceCaption,
    primaryAction,
    userMaxBidInPence,
  } = useAuctionCard(item, memberId, { isBuyNowTab: !isPlaceBidType });
  const isSellerOnly = isSeller && !isBuyer;

  const getHighestBid = useCallback(() => {
    if (highestBid !== null && highestBid !== undefined) {
      return highestBid;
    }

    return (
      pencesToPounds(item?.highestBidInPence) || pencesToPounds(item?.minimumPriceInPence) || 0
    );
  }, [highestBid, item.highestBidInPence, item.minimumPriceInPence]);

  const highestBidObj = item?.bids?.length && item.bids?.filter((bid) => bid.isHighest)?.[0];
  const isHighestBid = highestBidObj?.bidderOrganizationId === organizationId;

  const canShowPlaceBidButton = () =>
    (listType === auctionListType.MY_BIDS || listType === auctionListType.SEARCH) &&
    item?.organizationId !== organizationId &&
    item?.seller?.organizationContact?.id !== organizationId &&
    item?.status === AuctionStatus.PUBLISHED;

  const [showMakeOfferModal, setShowMakeOfferModal] = useState(false);
  const [currentBidAuction, setCurrentBidAuction] = useState(null);
  const [currentBidLocationId, setCurrentBidLocationId] = useState(null);

  const handleBidModal = (values) => {
    if (!values?.location?.value) {
      dispatch(showAlert('Please select a location.', AlertVariant.DANGER));
      return;
    }
    setCurrentBidAuction(+values.amount);
    setCurrentBidLocationId(values.location.value);
    setShowMakeOfferModal(true);
  };

  const handleMakeOffer = (amount) => {
    if (isPlaceBidType) {
      dispatch(placeBid(item?.id, amount * 100, null, currentBidLocationId));
    } else {
      dispatch(buyNowAuction(item?.id, null, currentBidLocationId));
    }
    setShowMakeOfferModal(false);
  };

  const getProxyBid = useCallback(() => {
    if (proxyBid !== null && proxyBid !== undefined) {
      return proxyBid;
    }

    return pencesToPounds(item.proxyBidInPence);
  }, [proxyBid, item.proxyBidInPence]);

  const renderCurrentBid = useCallback(() => {
    const value = getProxyBid() || getHighestBid();
    const priceGreenCondition =
      shouldPriceBeGreen !== null ? shouldPriceBeGreen : item?.shouldPriceBeGreen;

    const isReservePrice = () =>
      !!item.type && item.type === AuctionStatusType.PHYSICAL && !!item.autoAcceptPriceInPence;

    let bidLabel = (
      <p className="m-0 main-description-container__item-title text-xs">Current Bid</p>
    );

    if (
      item.type === AuctionStatusType.PHYSICAL ||
      item.type === AuctionStatusType.MINIMUM_PRICE
    ) {
      bidLabel = priceGreenCondition ? (
        <p className="m-0 main-description-container__item-title text-xs bid-price-label__green">
          Reserve Met
        </p>
      ) : (
        <p
          className={`m-0 main-description-container__item-title ${
            item.type === AuctionStatusType.PHYSICAL
              ? 'bid-label-black-color'
              : 'bid-label-danger-color'
          } text-xs`}
        >
          {item.type === AuctionStatusType.PHYSICAL ? 'Reserve Not Met' : 'No Reserve Sale'}
        </p>
      );
    }

    return (
      <div>
        <p
          className={`bid-price-label ${
            priceGreenCondition ? 'bid-price-label__green' : ''
          } m-0`}
        >
          <CurrencyFormat
            value={value || 100}
            thousandSeparator={true}
            allowNegative={false}
            prefix="£"
            displayType="text"
          />
        </p>
        {bidLabel}
        {isDetailsPage && isReservePrice() && (
          <div className="text-xs">
            <span className="main-description-container__item-title">{`Reserve Price: `}</span>
            <span className="summary__auction-price">
              {!!item && (
                <CurrencyFormat
                  value={pencesToPounds(item.autoAcceptPriceInPence)}
                  thousandSeparator={true}
                  allowNegative={false}
                  prefix="£"
                  displayType="text"
                />
              )}
            </span>
          </div>
        )}
      </div>
    );
  }, [getHighestBid, isDetailsPage, item, getProxyBid]);

  const renderAuctionEnd = () => {
    const getTitle = () => {
      if (
        item?.status === AuctionStatus.SOLD ||
        item?.status === AuctionStatus.ENDED ||
        item?.status === AuctionStatus.CANCELLED
      ) {
        return 'Auction Ended';
      }

      return 'Auction Ends';
    };

    return (
      <div>
        <p className="font-weight-bold text-base auction-end m-0">
          {item?.endsOn ? moment(item?.endsOn).format('DD/MM/YY  HH:mmA') : 'N/A'}
        </p>
        <p className="m-0 main-description-container__item-title text-xs">{getTitle()}</p>
      </div>
    );
  };

  const renderTimeLeft = () => (
    <div>
      <AuctionTimeLabel
        auctionTimestamp={auctionTimestamp || item?.timeStamp}
        item={item}
        listType={auctionListType.MY_VEHICLES}
        organizationId={organizationId}
        windowFocus={windowFocus}
      />
      <p className="m-0 main-description-container__item-title text-xs">Time Left</p>
    </div>
  );

  const renderBoxVariant = () => {
    if (isFinishedAuction) {
      return BoxStatusVariants.SECONDARY;
    }
    if (
      isLostBidder ||
      bidsSearchFilter === BidStatus.LOST ||
      ((item?.status === AuctionStatus.TO_ACCEPT || item?.status === AuctionStatus.REJECTED) &&
        item?.bids?.filter((it) => it.isHighest).length === 0)
    ) {
      return BoxStatusVariants.DANGER;
    }
    if (item?.status === AuctionStatus.TO_ACCEPT || item?.status === AuctionStatus.WAITING) {
      return (isHighestBid && item?.isBuyerAcceptanceAllowed) ||
        (item?.seller?.id === memberId && item?.isSellerAcceptanceAllowed)
        ? BoxStatusVariants.SUCCESS
        : BoxStatusVariants.WARNING;
    }
    return null;
  };

  const renderStatusBoxContent = () => {
    if (isFinishedAuction) {
      return AuctionStatusName.ENDED;
    }
    if (
      isLostBidder ||
      bidsSearchFilter === BidStatus.LOST ||
      ((item?.status === AuctionStatus.TO_ACCEPT || item?.status === AuctionStatus.REJECTED) &&
        item?.bids?.filter((it) => it.isHighest).length === 0)
    ) {
      return BidStatus.LOST;
    }
    if (
      (listType === auctionListType.MY_BIDS || listType === auctionListType.SEARCH) &&
      item?.status === AuctionStatus.SOLD &&
      isHighestBid
    ) {
      return 'Won';
    }
    if (
      item?.status === AuctionStatus.TO_ACCEPT &&
      context === AuctionListTemplateContextType.BUYING
    ) {
      return `${AuctionStatusName.TO_ACCEPT} - Waiting for the seller's acceptance`;
    }
    if (
      isHighestBid &&
      (item?.status === AuctionStatus.TO_ACCEPT || item?.status === AuctionStatus.WAITING)
    ) {
      return item?.isBuyerAcceptanceAllowed
        ? AuctionStatusName.TO_ACCEPT
        : AuctionStatusName.WAITING;
    }
    return undefined;
  };

  const isLostBidder =
    (item?.type === AuctionStatusType.TRYING_TO_DEAL || item?.status === AuctionStatus.SOLD) &&
    item?.status !== AuctionStatus.PUBLISHED &&
    item?.seller?.id !== memberId &&
    item?.bids.length > 0 &&
    !item?.bids?.filter((it) => it.isHighest).length;

  const isFinishedAuction =
    item?.type === AuctionStatusType.TRYING_TO_DEAL &&
    item?.status !== AuctionStatus.PUBLISHED &&
    item?.status !== AuctionStatus.SOLD &&
    item?.seller?.id !== memberId &&
    item?.bids.length === 0;

  const isValidPence = (n) => typeof n === 'number' && Number.isFinite(n) && n >= 0;

  const getInitBidValue = useCallback(() => {
    const baseInPence =
      isValidPence(item?.proxyBidInPence) && item.proxyBidInPence > 0
        ? item.proxyBidInPence
        : item?.highestBidInPence;
    if (isValidPence(baseInPence) && baseInPence > 0) {
      return baseInPence / 100 + pencesToPounds(AUCTION_BID_MINIMUM_AMOUNT_IN_PENCE);
    }
    if (isValidPence(item?.minimumPriceInPence)) {
      return pencesToPounds(item?.minimumPriceInPence);
    }
    return pencesToPounds(15000);
  }, [item]);

  const baseInPence = getBaseBidInPence(item, bidContext, userMaxBidInPence);

  const bidMinValue = () => {
    const min = item?.minimumPriceInPence;
    const highest = item?.highestBidInPence;
    const proxy = item?.proxyBidInPence;

    if (isValidPence(min) && (!isValidPence(highest) || highest === 0)) {
      return min / 100;
    }
    if (!isValidPence(min) && !isValidPence(highest) && !isValidPence(proxy)) {
      return 15000 / 100;
    }
    return (baseInPence + 5000) / 100;
  };

  const handleFastBidClick = (inc, location = null) => {
    const proxy = item?.proxyBidInPence;
    const highest = item?.highestBidInPence;
    const min = item?.minimumPriceInPence;
    const isWinningBidder = item?.bids?.find((it) => it.isHighest)?.bidderId === memberId;
    let basePounds = 0;
    if (isValidPence(proxy) && proxy > 0 && !isWinningBidder) basePounds = proxy / 100;
    else if (isValidPence(highest) && highest > 0) basePounds = highest / 100;
    else if (isValidPence(min)) basePounds = min / 100;
    else basePounds = 15000 / 100;
    handleBidModal({ amount: basePounds + inc, location });
  };

  const validationSchema = Yup.object().shape({
    amount: Yup.number()
      .required('Amount value is required')
      .integer('Bid must be an integer')
      .min(
        bidMinValue(),
        `Your bid must be greater than or equal to ${preparePriceFormat(bidMinValue(), false)}`,
      ),
  });

  const [fastBiddingButton, setFastBiddingButtons] = useState([50, 100, 150, 200]);

  useEffect(() => {
    if (item) {
      setShowNextGearBuyerSuccessAlert(
        isWonBidder && isSoldAuction && nextGearRequestSucceeded && !isLostBidder,
      );
      setShowNextGearBuyerFailureAlert(
        isWonBidder && isSoldAuction && nextGearRequestFailed && !isLostBidder,
      );
    }
  }, [item]);

  useEffect(() => {
    if (!item || !item.endsOn || item?.status !== AuctionStatus.PUBLISHED) {
      return () => {};
    }

    const checkTime = () => {
      const now = Date.now();
      const endsOnTimestamp = new Date(item.endsOn).getTime();
      const difference = endsOnTimestamp - now;
      const oneHourInMilliseconds = 60 * 60 * 1000;

      if (difference > 0 && difference < oneHourInMilliseconds) {
        setFastBiddingButtons([50, 75, 100]);
      }
    };

    checkTime();
    const timerId = setInterval(checkTime, 1000);

    return () => clearInterval(timerId);
  }, [item, setFastBiddingButtons]);

  useLayoutEffect(() => {
    if (showMakeOfferModal) {
      document.body.classList.add('relaunch-popup-active');
    } else {
      document.body.classList.remove('relaunch-popup-active');
    }
    return () => {
      document.body.classList.remove('relaunch-popup-active');
    };
  }, [showMakeOfferModal]);

  const renderStatusBox = () =>
    !(
      item?.status === AuctionStatus.PUBLISHED &&
      moment(item.endsOn).isBefore() &&
      item?.sellerId !== memberId &&
      item?.bids.length === 0
    ) && (
      // eslint-disable-next-line react/jsx-no-useless-fragment
      <>
        {(() => {
          if (canShowPlaceBidButton() && !isSellerOnly) {
            return (
              <Formik
                innerRef={bidFormRef}
                initialValues={{
                  amount: '',
                  location: isLocationLocked ? lockedLocationOption : defaultLocationOption,
                }}
                onSubmit={(values, { resetForm }) => {
                  // eslint-disable-next-line no-unused-expressions
                  if (isPlaceBidType) {
                    handleBidModal(values);
                  } else {
                    if (!values?.location?.value) {
                      dispatch(showAlert('Please select a location.', AlertVariant.DANGER));
                      return undefined;
                    }
                    setCurrentBidLocationId(values.location.value);
                    setShowMakeOfferModal(true);
                  }
                  return resetForm({ values: { amount: '', location: values.location } });
                }}
                validationSchema={isPlaceBidType ? validationSchema : null}
              >
                {(form) => (
                  <Form autoComplete="off">
                    <div className="d-flex gap-2 justify-content-between bid-box-wrapper">
                      {isPlaceBidType && (
                        <FormCurrencyInputFieldFixed
                          maxLength={14}
                          name="amount"
                          label="Bid"
                          disabled={!isBidAllowed}
                          labelVariant="bordered"
                          className="w-50"
                          prefix="£"
                          isErrorVisible={false}
                          placeholder={`Minimum ${preparePriceFormat(bidMinValue(), false)}`}
                        />
                      )}
                      <CommonButton
                        variant={CommonButtonVariants.RED}
                        label={isPlaceBidType ? 'Place bid' : 'Buy now'}
                        className={isPlaceBidType ? 'w-50' : 'w-100'}
                        icon={auctionIcon}
                        iconAlt="Auction icon"
                        iconLeft
                        disabled={
                          (listType !== auctionListType.SEARCH && isLoading) ||
                          item?.status !== AuctionStatus.PUBLISHED ||
                          !isBidAllowed ||
                          (isSeller && !isBuyer)
                        }
                      />
                    </div>
                    {isPlaceBidType && (form.submitCount > 0 || form.touched.amount) && (
                      <label className="place-bid-input form-input-field__label error-label mt-0 mb-2 ms-2">
                        {form.errors.amount || ''}
                      </label>
                    )}
                    {isPlaceBidType && (
                      <div className="fast-bid-btn-wrapper d-flex">
                        {fastBiddingButton.map((inc) => (
                          <button
                            type="button"
                            className="fast-bid-btn"
                            onClick={() => handleFastBidClick(inc, form.values.location)}
                          >
                            +£{inc}
                          </button>
                        ))}
                      </div>
                    )}
                    <div className="mt-26">
                      <FormSelectField
                        name="location"
                        label="Location"
                        options={locationOptions}
                        disabled={isLocationLocked || hasSingleLocation}
                        onChangeHandler={(location) => {
                          setLocationIdCookie(location.value);
                        }}
                        labelVariant="bordered"
                        selectProps={{ menuPosition: 'fixed' }}
                      />
                    </div>
                  </Form>
                )}
              </Formik>
            );
          }
          const isOwnerAction =
            primaryAction?.kind?.startsWith('owner') ||
            primaryAction?.kind === 'restricted_seller_live';
          if (isOwnerAction) {
            return (
              <div className="d-flex gap-3">
                <CommonButton
                  variant={primaryAction?.variant || CommonButtonVariants.LIVE}
                  label={primaryAction?.label || 'LIVE'}
                  className="w-100 mb-3"
                  icon={primaryAction?.icon}
                  iconAlt={primaryAction?.iconAlt}
                  iconLeft
                  disabled={true}
                />
              </div>
            );
          }
          return (
            <AuctionStatusBox
              auction={item}
              content={renderStatusBoxContent()}
              customBoxVariant={renderBoxVariant()}
            />
          );
        })()}
      </>
    );

  const renderPattern = () => {
    const finalPattern = pattern || DefaultPattern;
    const getColSize = (index) => {
      if (patternSizes?.length === finalPattern?.length) {
        return patternSizes[index];
      }

      return 4;
    };

    return finalPattern.map((el, index) => {
      let content;
      switch (el) {
        case SlotItems.CURRENT_BID:
          content = renderCurrentBid();
          break;
        case SlotItems.TIME_LEFT:
          content = renderTimeLeft();
          break;
        case SlotItems.AUCTION_END:
          content = renderAuctionEnd();
          break;
        case SlotItems.AUCTION_STATUS_BOX:
          content = renderStatusBox();
          break;
        default:
          content = el; // custom content
      }

      return (
        <Col
          sm={12}
          md={getColSize(index)}
          lg={getColSize(index)}
          className="d-flex justify-content-start align-items-center p-0 main-description-pattern-item"
        >
          {content}
        </Col>
      );
    });
  };

  const bidBar = (() => {
    if (!bidContext.show || !bidContext.message) return null;
    const barClass =
      bidContext.variant === 'green'
        ? 'auction-list-item__bid-bar__green'
        : 'auction-list-item__bid-bar__red';
    return (
      <div className={`auction-list-item__bid-bar ${barClass}`}>
        <span>{bidContext.message}</span>
      </div>
    );
  })();

  const canBidAuction =
    (listType === auctionListType.MY_BIDS || listType === auctionListType.SEARCH) &&
    item?.organizationId !== organizationId &&
    item?.seller?.organizationContact?.id !== organizationId &&
    item?.status === AuctionStatus.PUBLISHED &&
    item?.isBiddingAllowed;
  const canBidTabs = canBidAuction && !isSellerOnly;

  const _handlePlaceBidClick = () => {
    setIsPlaceBidType(true);
  };

  const physicalAuctionCaption = `Reserve Price: ${formatGBP(item.autoAcceptPriceInPence)}${
    item?.buyNowPriceInPence ? `. Buy now price: ${formatGBP(item.buyNowPriceInPence)}` : ''
  }`;

  const isAuctionOwner = item?.seller?.id === memberId;

  const handleNextGearAction = async () => {
    if (
      nextGearLoading ||
      !hasSellerNextGearId ||
      !isSoldAuction ||
      isLostBidder ||
      nextGearRequestSucceeded
    ) {
      return;
    }

    if (!buyerNextGearId) {
      const url = process.env.REACT_APP_NEXT_GEAR_URL;
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
      return;
    }

    setNextGearLoading(true);
    try {
      const response = await requestNextGearLoanCoverage(item.id);
      const loanCoverageStatus = response.data;

      setShowNextGearBuyerSuccessAlert(
        loanCoverageStatus === NextGearLoanCoverageStatus.SUCCEEDED,
      );
      setShowNextGearBuyerFailureAlert(
        loanCoverageStatus === NextGearLoanCoverageStatus.FAILED,
      );
    } catch (err) {
      const message = err?.response?.data?.message;
      dispatch(showAlert(message, AlertVariant.DANGER));
    } finally {
      setNextGearLoading(false);
    }
  };

  const handleRedirectToMovex = () => {
    const url = process.env.REACT_APP_MOVEX_URL;
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const hasSellerNextGearId = item.seller.organizationContact?.sellerNextGearId;
  const isOwnAuction = item?.seller.id === memberId;
  const isWonBidder = item?.buyer?.id === memberId;
  const isSoldAuction = item?.status === AuctionStatus.SOLD;
  const nextGearRequestSucceeded =
    item.nextGearLoanCoverageStatus === NextGearLoanCoverageStatus.SUCCEEDED;
  const nextGearRequestFailed =
    item.nextGearLoanCoverageStatus === NextGearLoanCoverageStatus.FAILED;

  return (
    <div className="d-flex flex-column">
      {showNextGearBuyerSuccessAlert && (
        <Alert color="success">NextGear Capital stock funding request successful</Alert>
      )}
      {showNextGearBuyerFailureAlert && (
        <Alert color="danger">
          Unfortunately, your NextGear Capital stock funding request has failed, please contact
          the NextGear Customer Service Team on +443435060600
        </Alert>
      )}
      {isOwnAuction && isSoldAuction && nextGearRequestSucceeded && (
        <Alert color="success">
          Buyer has paid using their NextGear Capital stock funding facility
        </Alert>
      )}
      {isOwnAuction && isSoldAuction && nextGearRequestFailed && (
        <Alert color="danger">
          NextGear Capital stock funding is unavailable. The buyer must use an alternative
          payment method.
        </Alert>
      )}
      {!isOwnAuction && (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <div
          className={`next-gear-capital-badge d-flex mb-3 mt-sm-4 mt-lg-0 ${nextGearLoading ? 'opacity-50' : 'opacity-100'}`}
          onClick={handleNextGearAction}
        >
          <img src={nextGearCapitalImg} alt="NGC logo" />
          {hasSellerNextGearId ? (
            <div className="pb-1">
              <span className="caption caption__available">Available</span>
              <img src={availableIcon} alt="Available - check icon" />
            </div>
          ) : (
            <div className="pb-1">
              <span className="caption">Not Available</span>
              <img src={notAvailableIcon} alt="Not Available - check icon" />
            </div>
          )}
        </div>
      )}
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div className="movex-badge mb-4 py-3" onClick={handleRedirectToMovex}>
        <span className="caption">Order vehicle transport via</span>
        <img src={movexLogo} alt="Movex logo" />
      </div>

      <div
        className={`d-flex flex-column justify-content-center align-items-center flex-grow-1 ${
          isDetailsPage
            ? 'main-description-container__info-bar'
            : 'main-description-container__basic-data'
        } ${className}`}
      >
        <div className="d-flex w-100 p-0">
          {canBidTabs && item?.type !== AuctionStatusType.BUY_NOW_ONLY ? (
            <button
              type="button"
              onClick={_handlePlaceBidClick}
              className={`flex-grow-1 header tab ${isPlaceBidType ? 'active' : ''}`}
            >
              <img
                src={isPlaceBidType ? auctionIcon : auctionBlackIcon}
                alt="Place bid icon"
              />
              <span>Place bid</span>
            </button>
          ) : null}
          {canBidTabs &&
          (item?.type === AuctionStatusType.BUY_NOW_ONLY || item?.isBuyNowFeatureEnable) ? (
            <button
              type="button"
              onClick={() => setIsPlaceBidType(false)}
              className={`flex-grow-1 header tab ${!isPlaceBidType ? 'active' : ''}`}
            >
              <img src={isPlaceBidType ? buyNowIcon : buyNowWhiteIcon} alt="Buy now icon" />
              <span>Buy now</span>
            </button>
          ) : null}
          {!canBidTabs && (
            <div className="header header--seller">
              <img src={infoWhiteIcon} alt="Info icon" />
              <span>Offer status</span>
            </div>
          )}
        </div>

        {showMakeOfferModal && (
          <PortalSweetAlert
            showCancel
            show={showMakeOfferModal}
            onConfirm={() => handleMakeOffer(currentBidAuction)}
            onCancel={() => setShowMakeOfferModal(false)}
            cancelBtnCssClass="common-button outline-primary-red"
            cancelBtnText="Cancel"
            confirmBtnText={isPlaceBidType ? 'Place bid' : 'Buy now'}
            customIcon={<img src={closeIcon} alt="Close icon" />}
            confirmBtnCssClass="common-button red-button"
            title=""
            customClass="make-offer-modal"
          >
            <div className="sweet-alert__header">
              <span className="sweet-alert-text">
                {isPlaceBidType ? 'Confirm your bid' : 'Confirm your purchase'}
              </span>
              <button type="button" onClick={() => setShowMakeOfferModal(false)}>
                <img src={closeIcon} alt="Close icon" />
              </button>
            </div>
            <div className="sweet-alert__content">
              <span>
                {isPlaceBidType
                  ? 'Please review and confirm your bid:'
                  : 'Please review and confirm your purchase:'}
              </span>
              <div className="d-flex w-100 justify-content-around">
                <div className="sweet-alert__content-offer d-flex flex-column w-50">
                  <span>
                    {isPlaceBidType
                      ? formatGBP(currentBidAuction * 100)
                      : formatGBP(item?.buyNowPriceInPence)}
                  </span>
                  <span>{isPlaceBidType ? 'Your bid' : 'Buy now price'}</span>
                </div>
                {!isMobile && (
                  <div className="sweet-alert__content-offer d-flex flex-column w-50">
                    <span>
                      {item?.vehicle?.vehicleModel?.makeName}{' '}
                      {item?.vehicle?.vehicleModel?.name}
                    </span>
                    <span>Vehicle</span>
                  </div>
                )}
              </div>
              <div className="mt-4 w-100">
                <Formik
                  enableReinitialize
                  initialValues={{
                    location:
                      locationOptions.find((opt) => opt.value === currentBidLocationId) ||
                      null,
                  }}
                  onSubmit={() => {}}
                >
                  <Form autoComplete="off">
                    <FormSelectField
                      name="location"
                      label="Location"
                      options={locationOptions}
                      disabled={isLocationLocked || hasSingleLocation}
                      onChangeHandler={(location) => {
                        setCurrentBidLocationId(location?.value || null);
                        if (location?.value) {
                          setLocationIdCookie(location.value);
                        }
                      }}
                      labelVariant="bordered"
                      selectProps={{ menuPlacement: 'auto' }}
                    />
                  </Form>
                </Formik>
              </div>
            </div>
          </PortalSweetAlert>
        )}
        <Row sm={12} className="main-description-pattern-wrapper">
          <div className="main-description-pattern-wrapper-inner">
            {isPlaceBidType && bidBar && <div className="mb-3">{bidBar}</div>}
            {isAuctionOwner && item?.type === AuctionStatusType.PHYSICAL && (
              <div className="mb-3">
                <div className="auction-list-item__bid-bar auction-list-item__bid-bar__primary">
                  <span>{physicalAuctionCaption}</span>
                </div>
              </div>
            )}
            {isAuctionOwner &&
              item?.type === AuctionStatusType.MINIMUM_PRICE &&
              item?.buyNowPriceInPence && (
                <div className="mb-3">
                  <div className="auction-list-item__bid-bar auction-list-item__bid-bar__primary">
                    <span>Buy now price: {formatGBP(item.buyNowPriceInPence)}</span>
                  </div>
                </div>
              )}
            <div className={`d-flex flex-row `}>
              <div className="auction-list-item__price-info">
                <span>
                  {!isPlaceBidType
                    ? formatGBP(item?.buyNowPriceInPence)
                    : formatGBP(displayPriceInPence)}
                </span>
                <span>
                  {priceCaption || (isPlaceBidType ? 'Current bid' : 'Buy now price')}
                </span>
              </div>
              <div className="auction-list-item__time-info time-info">
                <div className="auction-details__countdown-wrapper">
                  <AuctionCountdown item={item} context={context} />
                </div>
              </div>
            </div>
            <div className="status-box">{renderStatusBox()}</div>
          </div>
          {status && isPlaceBidType && (
            <span className={`status ${status.className}`}>{status.text}</span>
          )}
        </Row>
      </div>
    </div>
  );
}

AuctionMainDescriptionContainer.defaultProps = {
  organizationId: '',
  windowFocus: false,
  listType: '',
  pattern: [],
  patternSizes: [],
  isDetailsPage: false,
  bidBarMessage: '',
  bidBarColor: '',
  className: '',
  auctionTimestamp: null,
  highestBid: null,
  proxyBid: null,
  shouldPriceBeGreen: null,
};

AuctionMainDescriptionContainer.propTypes = {
  item: PropTypes.object.isRequired,
  organizationId: PropTypes.string,
  windowFocus: PropTypes.bool,
  listType: PropTypes.string,
  pattern: PropTypes.array,
  patternSizes: PropTypes.array,
  isDetailsPage: PropTypes.bool,
  bidBarMessage: PropTypes.string,
  bidBarColor: PropTypes.string,
  className: PropTypes.string,
  auctionTimestamp: PropTypes.object,
  highestBid: PropTypes.number,
  proxyBid: PropTypes.number,
  shouldPriceBeGreen: PropTypes.bool,
};

export default AuctionMainDescriptionContainer;
